import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
  Fade,
  Chip,
  Avatar,
  TablePagination,
  Paper,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  MenuItem
} from '@mui/material';
import Fab from '@mui/material/Fab';
import Grid from '@mui/material/Grid';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useLocation, useNavigate } from 'react-router-dom';

import AddSalon from '../../pro/salon/addSalon';
import SalonCard from '../components/SalonCard'; // Imported new component
import LocationStatusChip from '../components/LocationStatusChip';
import { superadminService } from '../../../services/superadmin.service';
import { theme } from '../../../theme';

function useQueryParam(name) {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search).get(name) || '', [search, name]);
}

const SalonsList = () => {
  const navigate = useNavigate();
  const urlSearch = useQueryParam('search');
  const urlLocationStatus = useQueryParam('location_status');

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const [search, setSearch] = useState(urlSearch);
  const [submittedSearch, setSubmittedSearch] = useState(urlSearch);

  const [viewMode, setViewMode] = useState('cards');
  const [createOpen, setCreateOpen] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [salonToDelete, setSalonToDelete] = useState(null);
  const [archivedOnly, setArchivedOnly] = useState(false);
  const [locationStatus, setLocationStatus] = useState(urlLocationStatus);
  const [locationSummary, setLocationSummary] = useState(null);
  const [selectedSalonIds, setSelectedSalonIds] = useState(() => new Set());
  const [bulkRepairLoading, setBulkRepairLoading] = useState(false);

  useEffect(() => {
    setPage(0);
    setLocationStatus(urlLocationStatus);
  }, [urlLocationStatus]);

  // Toast State
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const showToast = (message, severity = 'success') => {
    setToast({ open: true, message, severity });
  };

  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') return;
    setToast({ ...toast, open: false });
  };

  const load = async (termOverride) => {
    try {
      setError('');
      setLoading(true);
      const term = typeof termOverride === 'string' ? termOverride : submittedSearch;
      const res = await superadminService.listSalons({
        search: term,
        page: page + 1,
        page_size: pageSize,
        include_archived: archivedOnly ? 1 : 0,
        archived_only: archivedOnly ? 1 : 0,
        location_status: locationStatus || undefined,
      });
      const data = res?.data;
      if (data?.results) {
        setItems(data.results);
        setTotal(data.count || 0);
      } else {
        setItems(data || []);
        setTotal(Array.isArray(data) ? data.length : 0);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [submittedSearch, page, pageSize, archivedOnly, locationStatus]);

  const loadLocationSummary = async () => {
    try {
      const response = await superadminService.getSalonLocationSummary();
      setLocationSummary(response?.data || null);
    } catch (summaryError) {
      console.error('Location summary error:', summaryError);
    }
  };

  useEffect(() => {
    loadLocationSummary();
  }, [archivedOnly]);

  const groupedByOwner = useMemo(() => {
    const groups = new Map();
    (items || []).forEach((s) => {
      const owner = s?.owner;
      // Use email as fallback key if id is missing, or 'unknown'
      const key = owner?.id ? String(owner.id) : (owner?.email || 'unknown');

      if (!groups.has(key)) {
        groups.set(key, { owner, salons: [] });
      }
      groups.get(key).salons.push(s);
    });
    return Array.from(groups.values());
  }, [items]);

  const rows = useMemo(() => items || [], [items]);
  const selectedCount = selectedSalonIds.size;
  const allRowsSelected = rows.length > 0
    && rows.every((salon) => selectedSalonIds.has(salon.salon_id));

  const toggleSalonSelection = (salonId) => {
    setSelectedSalonIds((current) => {
      const next = new Set(current);
      if (next.has(salonId)) next.delete(salonId);
      else next.add(salonId);
      return next;
    });
  };

  const toggleCurrentPage = () => {
    setSelectedSalonIds((current) => {
      const next = new Set(current);
      if (allRowsSelected) {
        rows.forEach((salon) => next.delete(salon.salon_id));
      } else {
        rows.forEach((salon) => next.add(salon.salon_id));
      }
      return next;
    });
  };

  const repairSelectedSalons = async (forceRefresh = false) => {
    if (selectedSalonIds.size === 0) return;
    try {
      setBulkRepairLoading(true);
      const response = await superadminService.bulkRepairSalonLocations({
        salon_ids: Array.from(selectedSalonIds),
        force_refresh: forceRefresh,
      });
      const queued = response?.data?.queued ?? selectedSalonIds.size;
      const deduplicated = response?.data?.deduplicated ?? 0;
      showToast(
        `${queued} réparation(s) mise(s) en file${deduplicated ? `, dont ${deduplicated} déjà en cours` : ''}.`,
        'success',
      );
      setSelectedSalonIds(new Set());
      await Promise.all([load(submittedSearch), loadLocationSummary()]);
    } catch (bulkError) {
      showToast(
        bulkError?.response?.data?.detail || 'Impossible de relancer les localisations sélectionnées.',
        'error',
      );
    } finally {
      setBulkRepairLoading(false);
    }
  };

  const handleOpenDelete = (salon) => {
    setDeleteError('');
    setSalonToDelete(salon);
    setDeleteOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!salonToDelete?.salon_id) return;
    try {
      setDeleteLoading(true);
      setDeleteError('');
      await superadminService.archiveSalon(salonToDelete.salon_id);
      showToast('Salon archivé.', 'success');
      setDeleteOpen(false);
      setSalonToDelete(null);
      await load(submittedSearch);
    } catch (e) {
      setDeleteError(e?.response?.data?.detail || "Erreur lors de l'archivage du salon.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRestoreSalon = async () => {
    if (!salonToDelete?.salon_id) return;
    try {
      setDeleteLoading(true);
      setDeleteError('');
      await superadminService.restoreSalon(salonToDelete.salon_id);
      showToast('Salon restauré.', 'success');
      setDeleteOpen(false);
      setSalonToDelete(null);
      await load(submittedSearch);
    } catch (e) {
      setDeleteError(e?.response?.data?.detail || 'Erreur lors de la restauration du salon.');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handlePurgeSalon = async () => {
    if (!salonToDelete?.salon_id) return;
    try {
      setDeleteLoading(true);
      setDeleteError('');
      await superadminService.purgeSalon(salonToDelete.salon_id);
      showToast('Salon supprimé définitivement.', 'success');
      setDeleteOpen(false);
      setSalonToDelete(null);
      await load(submittedSearch);
    } catch (e) {
      setDeleteError(e?.response?.data?.detail || 'Erreur lors de la suppression définitive du salon.');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto', pb: 12 }}>
      {/* Header Section */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 4,
          flexWrap: 'wrap'
        }}
      >
        <Box sx={{ mb: { xs: 0.5, md: 0 } }}>
          <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.25rem', md: '1.75rem' }, color: theme.colors.text.primary, mb: 0.25 }}>
            Salons
          </Typography>
          <Typography variant="body2" sx={{ color: theme.colors.text.secondary, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
            Gérez l'ensemble des salons partenaires
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Button
            variant={archivedOnly ? 'outlined' : 'text'}
            color={archivedOnly ? 'warning' : 'inherit'}
            onClick={() => {
              setPage(0);
              setArchivedOnly((prev) => !prev);
            }}
            sx={{ textTransform: 'none', fontWeight: 800 }}
          >
            {archivedOnly ? 'Voir actifs' : 'Voir archivés'}
          </Button>

          <ToggleButtonGroup
            exclusive
            value={viewMode}
            onChange={(_, v) => {
              if (v) setViewMode(v);
            }}
            size="small"
            sx={{
              bgcolor: 'white',
              border: `1px solid ${theme.colors.border.light}`,
              borderRadius: theme.borderRadius.lg,
              height: { xs: 36, sm: 48 },
              '& .MuiToggleButton-root': {
                border: 'none',
                borderRadius: theme.borderRadius.lg,
                px: { xs: 1, sm: 2 }
              }
            }}
          >
            <ToggleButton value="list">
              <ViewListIcon fontSize="small" sx={{ mr: { xs: 0, sm: 1 } }} />
              <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'block' }, fontWeight: 700 }}>LISTE</Typography>
            </ToggleButton>
            <ToggleButton value="cards">
              <ViewModuleIcon fontSize="small" sx={{ mr: { xs: 0, sm: 1 } }} />
              <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'block' }, fontWeight: 700 }}>GRILLE</Typography>
            </ToggleButton>
          </ToggleButtonGroup>

          <Tooltip title="Ajouter un salon">
            <Fab
              color="primary"
              size="small"
              onClick={() => setCreateOpen(true)}
              sx={{
                width: { xs: 36, sm: 44 },
                height: { xs: 36, sm: 44 },
                boxShadow: '0 4px 12px rgba(22, 163, 74, 0.2)',
                '&:hover': {
                  boxShadow: '0 6px 16px rgba(22, 163, 74, 0.3)',
                }
              }}
            >
              <AddIcon fontSize="small" />
            </Fab>
          </Tooltip>
        </Box>
      </Box>

      {/* Search Bar */}
      <Card
        sx={{
          borderRadius: theme.borderRadius.xl,
          border: 'none',
          boxShadow: theme.boxShadow.sm,
          backgroundColor: '#fff',
          mb: 4,
          overflow: 'visible' // For shadow
        }}
      >
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              setSubmittedSearch(search.trim());
            }}
            sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
          >
            <Box sx={{ position: 'relative', flex: 1, display: 'flex', alignItems: 'center' }}>
              <SearchIcon sx={{ color: theme.colors.text.tertiary, fontSize: 22, ml: 2, mr: 1.5 }} />
              <TextField
                fullWidth
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher par nom, email, téléphone..."
                variant="standard"
                InputProps={{
                  disableUnderline: true,
                  style: { fontSize: '0.95rem', fontWeight: 500 }
                }}
              />
              {search && (
                <IconButton size="small" onClick={() => { setSearch(''); setSubmittedSearch(''); load(''); }} sx={{ mr: 1 }}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              )}
            </Box>
            <Button
              type="submit"
              variant="contained"
              sx={{
                borderRadius: theme.borderRadius.lg,
                textTransform: 'none',
                fontWeight: 700,
                backgroundColor: theme.colors.text.primary,
                color: '#fff',
                px: { xs: 1.5, sm: 3 },
                my: 0.5,
                mr: 0.5,
                '&:hover': { backgroundColor: '#000' }
              }}
            >
              Rechercher
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Card
        sx={{
          borderRadius: theme.borderRadius.xl,
          boxShadow: theme.boxShadow.sm,
          mb: 3,
        }}
      >
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              gap: 2,
              justifyContent: 'space-between',
              alignItems: { xs: 'stretch', lg: 'center' },
            }}
          >
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Chip
                label={`${locationSummary?.total ?? '—'} salons actifs`}
                variant="outlined"
              />
              <Chip
                color="error"
                label={`${locationSummary?.missing ?? '—'} sans localisation`}
                onClick={() => { setPage(0); setLocationStatus('missing'); }}
              />
              <Chip
                color="warning"
                label={`${locationSummary?.needs_review ?? '—'} à vérifier`}
                onClick={() => { setPage(0); setLocationStatus('needs_review'); }}
              />
              <Chip
                color="error"
                variant="outlined"
                label={`${locationSummary?.failed ?? '—'} en échec`}
                onClick={() => { setPage(0); setLocationStatus('failed'); }}
              />
              <Chip
                color="success"
                variant="outlined"
                label={`${locationSummary?.complete ?? '—'} complètes`}
                onClick={() => { setPage(0); setLocationStatus('complete'); }}
              />
            </Box>

            <TextField
              select
              size="small"
              label="Statut de localisation"
              value={locationStatus}
              onChange={(event) => {
                setPage(0);
                setSelectedSalonIds(new Set());
                setLocationStatus(event.target.value);
              }}
              sx={{ minWidth: 220 }}
            >
              <MenuItem value="">Tous les statuts</MenuItem>
              <MenuItem value="missing">Localisation absente</MenuItem>
              <MenuItem value="pending">À traiter</MenuItem>
              <MenuItem value="complete">Complète</MenuItem>
              <MenuItem value="needs_review">À vérifier</MenuItem>
              <MenuItem value="failed">Échec</MenuItem>
            </TextField>
          </Box>

          {selectedCount > 0 && (
            <Alert
              severity="info"
              sx={{
                mt: 2,
                alignItems: 'center',
                '& .MuiAlert-message': { width: '100%' },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 1,
                  alignItems: { xs: 'stretch', sm: 'center' },
                  justifyContent: 'space-between',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 700 }}>
                  {selectedCount} salon(s) sélectionné(s)
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => repairSelectedSalons(false)}
                    disabled={bulkRepairLoading}
                  >
                    Relancer la sélection
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="warning"
                    onClick={() => repairSelectedSalons(true)}
                    disabled={bulkRepairLoading}
                  >
                    Forcer la sélection
                  </Button>
                </Box>
              </Box>
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Content Area */}
      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10 }}>
          <CircularProgress sx={{ color: theme.colors.primary[500], mb: 2 }} />
          <Typography sx={{ color: theme.colors.text.secondary }}>Chargement des salons...</Typography>
        </Box>
      ) : error ? (
        <Box sx={{ textAlign: 'center', py: 8, backgroundColor: theme.colors.error[50], borderRadius: theme.borderRadius.xl }}>
          <Typography sx={{ color: theme.colors.error[600], fontWeight: 700 }}>{error}</Typography>
          <Button startIcon={<RefreshIcon />} onClick={() => load()}>Réessayer</Button>
        </Box>
      ) : rows.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <Typography sx={{ color: theme.colors.text.secondary, fontWeight: 500, fontSize: '1.1rem' }}>
            Aucun salon trouvé pour cette recherche.
          </Typography>
        </Box>
      ) : viewMode === 'list' ? (
        <Fade in>
          <Card sx={{ borderRadius: theme.borderRadius.xl, overflow: 'hidden', boxShadow: theme.boxShadow.sm, border: `1px solid ${theme.colors.border.light}` }}>
            <Box sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: { xs: '100%', md: 800 } }}>
                <TableHead sx={{ backgroundColor: theme.colors.background.secondary }}>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allRowsSelected}
                        indeterminate={selectedCount > 0 && !allRowsSelected}
                        onChange={toggleCurrentPage}
                        inputProps={{ 'aria-label': 'Sélectionner les salons de cette page' }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700, color: theme.colors.text.secondary, textTransform: 'uppercase', fontSize: '0.75rem', py: 2 }}>Salon</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: theme.colors.text.secondary, textTransform: 'uppercase', fontSize: '0.75rem', py: 2 }}>Responsable</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: theme.colors.text.secondary, textTransform: 'uppercase', fontSize: '0.75rem', py: 2 }}>Localisation</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: theme.colors.text.secondary, textTransform: 'uppercase', fontSize: '0.75rem', py: 2, display: { xs: 'none', md: 'table-cell' } }}>Stats</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: theme.colors.text.secondary, textTransform: 'uppercase', fontSize: '0.75rem', py: 2 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((s) => {
                    const owner = s?.owner;
                    const ownerLabel = owner
                      ? `${owner.first_name || ''} ${owner.last_name || ''}`.trim() || owner.email
                      : '—';
                    return (
                      <TableRow
                        key={s.salon_id}
                        hover
                        sx={{ cursor: 'pointer', transition: 'background-color 0.2s' }}
                        onClick={() => navigate(`/superadmin/salons/${s.salon_id}`)}
                      >
                        <TableCell
                          padding="checkbox"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <Checkbox
                            checked={selectedSalonIds.has(s.salon_id)}
                            onChange={() => toggleSalonSelection(s.salon_id)}
                            inputProps={{ 'aria-label': `Sélectionner ${s.name || 'ce salon'}` }}
                          />
                        </TableCell>
                        <TableCell sx={{ py: 2.5 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                              variant="rounded"
                              src={s.primary_photo || undefined}
                              sx={{
                                display: { xs: 'none', md: 'flex' },
                                width: 48,
                                height: 48,
                                backgroundColor: theme.colors.primary[100],
                                color: theme.colors.primary[600]
                              }}
                            >
                              {s.name?.[0] || 'S'}
                            </Avatar>
                            <Box>
                              <Typography sx={{ fontWeight: 700, color: theme.colors.text.primary, fontSize: '0.95rem' }}>
                                {s.name || 'Sans Nom'}
                              </Typography>
                              <Typography variant="caption" sx={{ color: theme.colors.text.secondary }}>
                                {s.city || 'Ville inconnue'}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 2.5 }}>
                          <Typography sx={{ fontWeight: 600, color: theme.colors.text.primary, fontSize: '0.90rem' }}>
                            {ownerLabel}
                          </Typography>
                          <Typography variant="caption" sx={{ color: theme.colors.text.tertiary }}>
                            {owner?.email || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 2.5 }}>
                          <LocationStatusChip
                            status={['queued', 'running'].includes(s.latest_location_repair?.status)
                              ? s.latest_location_repair.status
                              : s.location_status}
                          />
                          {s.location_missing_fields?.length > 0 && (
                            <Typography
                              variant="caption"
                              display="block"
                              color="text.secondary"
                              sx={{ mt: 0.5 }}
                            >
                              {s.location_missing_fields.join(', ')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ py: 2.5, display: { xs: 'none', md: 'table-cell' } }}>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Chip
                              size="small"
                              label={`${s?.employees_count ?? 0} Staff`}
                              sx={{ backgroundColor: theme.colors.primary[50], color: theme.colors.primary[700], fontWeight: 600 }}
                            />
                            <Chip
                              size="small"
                              label={`${s?.appointments_count ?? 0} RDV`}
                              sx={{ backgroundColor: theme.colors.secondary[50], color: theme.colors.secondary[800], fontWeight: 600 }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 2.5 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/superadmin/salons/${s.salon_id}`);
                            }}
                            sx={{
                              textTransform: 'none',
                              fontWeight: 700,
                              borderRadius: theme.borderRadius.md,
                              borderColor: theme.colors.border.light,
                              color: theme.colors.text.secondary,
                              '&:hover': {
                                borderColor: theme.colors.primary[500],
                                color: theme.colors.primary[500],
                                backgroundColor: theme.colors.primary[50]
                              }
                            }}
                          >
                            Détails
                          </Button>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenDelete(s);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Card>
        </Fade>
      ) : (
        <Fade in>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {groupedByOwner.map((group) => {
              const owner = group.owner;
              const ownerName = owner
                ? `${owner.first_name || ''} ${owner.last_name || ''}`.trim() || 'Nom inconnu'
                : 'Propriétaire inconnu';
              const ownerEmail = owner?.email || '';

              return (
                <Box
                  key={owner?.id || 'unknown'}
                  sx={{
                    p: 3,
                    border: `1px solid ${theme.colors.border.light}`,
                    borderRadius: theme.borderRadius.xl,
                    backgroundColor: theme.colors.background.secondary, // Light background for the group
                    position: 'relative'
                  }}
                >
                  {/* Owner Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
                    <Avatar
                      sx={{
                        width: 56,
                        height: 56,
                        bgcolor: theme.colors.secondary[300],
                        color: theme.colors.secondary[900],
                        fontWeight: 800,
                        fontSize: '1.5rem',
                        boxShadow: theme.boxShadow.md
                      }}
                    >
                      {ownerName.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: 800, color: theme.colors.text.primary }}>
                        {ownerName}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Typography variant="body2" sx={{ color: theme.colors.text.secondary }}>
                          {ownerEmail}
                        </Typography>
                        <Chip
                          label={`${group.salons.length} salon${group.salons.length > 1 ? 's' : ''}`}
                          size="small"
                          sx={{ ml: 1, height: 20, fontSize: '0.7rem', fontWeight: 700, backgroundColor: theme.colors.primary[100], color: theme.colors.primary[800] }}
                        />
                      </Box>
                    </Box>
                  </Box>

                  {/* Salons Grid for this Owner */}
                  <Grid container spacing={3}>
                    {group.salons.map((s) => (
                      <Grid key={s.salon_id} item xs={12} sm={6} md={4} lg={3}>
                        <SalonCard
                          salon={s}
                          onClick={() => navigate(`/superadmin/salons/${s.salon_id}`)}
                          onDelete={() => handleOpenDelete(s)}
                          selected={selectedSalonIds.has(s.salon_id)}
                          onSelect={() => toggleSalonSelection(s.salon_id)}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              );
            })}

            {groupedByOwner.length === 0 && (
              <Typography sx={{ textAlign: 'center', color: theme.colors.text.secondary, mt: 4 }}>
                Aucun résultat à afficher.
              </Typography>
            )}
          </Box>
        </Fade>
      )}

      {total > 0 && (
        <Box sx={{
          position: 'fixed',
          bottom: 0,
          right: 0,
          left: { xs: 0, md: '280px' },
          zIndex: 1000,
          p: 2,
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none'
        }}>
          <Paper
            elevation={3}
            sx={{
              pointerEvents: 'auto',
              borderRadius: theme.borderRadius.xl,
              border: `1px solid ${theme.colors.border.light}`,
              overflow: 'hidden',
              width: 'fit-content'
            }}
          >
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, newPage) => {
                setPage(newPage);
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="Lignes par page"
              sx={{ bgcolor: 'white' }}
            />
          </Paper>
        </Box>
      )}

      <AddSalon
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onSuccess={async () => {
          setCreateOpen(false);
          showToast('Salon créé avec succès.', 'success');
          await load(submittedSearch);
        }}
      />

      <Dialog open={deleteOpen} onClose={() => { if (!deleteLoading) { setDeleteOpen(false); setSalonToDelete(null); } }} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>{salonToDelete?.is_archived ? 'Gérer le salon archivé' : 'Archiver le salon'}</DialogTitle>
        <DialogContent>
          <Typography>
            {salonToDelete?.is_archived
              ? 'Choisissez une action pour '
              : 'Confirmez-vous l’archivage de '}
            <b>{salonToDelete?.name || 'ce salon'}</b> ?
          </Typography>
          {deleteError && <Alert severity="error" sx={{ mt: 2 }}>{deleteError}</Alert>}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setDeleteOpen(false); setSalonToDelete(null); }} disabled={deleteLoading}>Annuler</Button>
          {salonToDelete?.is_archived ? (
            <>
              <Button variant="outlined" onClick={handleRestoreSalon} disabled={deleteLoading}>
                {deleteLoading ? 'Restauration...' : 'Restaurer'}
              </Button>
              <Button variant="contained" color="error" onClick={handlePurgeSalon} disabled={deleteLoading}>
                {deleteLoading ? <CircularProgress size={24} color="inherit" /> : 'Supprimer définitivement'}
              </Button>
            </>
          ) : (
            <Button variant="contained" color="warning" onClick={handleConfirmDelete} disabled={deleteLoading}>
              {deleteLoading ? <CircularProgress size={24} color="inherit" /> : 'Archiver'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%', fontWeight: 600, borderRadius: theme.borderRadius.md }} variant="filled">
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SalonsList;
